package fr.manyverse.app.domain.usecase

import fr.manyverse.app.domain.image.ImageTarget
import fr.manyverse.app.domain.model.MemberRole
import fr.manyverse.app.domain.repository.AuthRepository
import fr.manyverse.app.domain.repository.CharacterRepository
import fr.manyverse.app.domain.repository.GameTableRepository
import fr.manyverse.app.domain.repository.ImageStorageRepository
import fr.manyverse.app.domain.repository.SignedUrlCacheRepository
import fr.manyverse.app.domain.repository.SoundRepository
import fr.manyverse.app.domain.repository.UniverseRepository

class DeleteAccountUseCase(
    private val universeRepository: UniverseRepository,
    private val characterRepository: CharacterRepository,
    private val imageStorageRepository: ImageStorageRepository,
    private val gameTableRepository: GameTableRepository,
    private val soundRepository: SoundRepository,
    private val authRepository: AuthRepository,
    private val signedUrlCacheRepository: SignedUrlCacheRepository
) {
    suspend operator fun invoke(userId: String): Result<Unit> {
        val universes = universeRepository.getCustomUniversesForUser(userId)
            .getOrElse { return Result.failure(it) }

        for (universe in universes) {
            // Delete characters (and their avatars)
            val characters = characterRepository
                .getCharactersByUserId(universe.id, userId)
                .getOrElse { emptyList() }
            for (character in characters) {
                if (character.imageUrl != null) {
                    imageStorageRepository.delete(ImageTarget.CharacterAvatar(userId, character.id))
                }
                characterRepository.deleteCharacter(character.id)
            }

            // Delete or leave active tables
            val tables = gameTableRepository
                .getUserActiveTables(universe.id, userId)
                .getOrElse { emptyList() }
            for ((table, role) in tables) {
                if (role == MemberRole.GAME_MASTER) {
                    gameTableRepository.deleteTable(table.id)
                } else {
                    gameTableRepository.leaveTable(table.id, userId)
                }
            }

            // Delete user-uploaded sounds
            val sounds = soundRepository
                .getSoundsByUniverseAndUser(universe.id, userId)
                .getOrElse { emptyList() }
            for (sound in sounds.filter { it.isUserUploaded }) {
                soundRepository.deleteSound(sound.id, userId)
            }
        }

        // Clear signed URL cache
        signedUrlCacheRepository.clear()

        // Delete Supabase Auth account and clear local session
        return authRepository.deleteAccount()
    }
}
